/** Same pixel size as the built-in homepage photo (`carbannerImg.jpg`, 2048×746). */
export const HOME_BANNER_WIDTH = 2048;
export const HOME_BANNER_HEIGHT = 746;
export const HOME_BANNER_MAX_BYTES = 3 * 1024 * 1024;
export const HOME_BANNER_MAX_WIDTH = 4096;

const TARGET_ASPECT = HOME_BANNER_WIDTH / HOME_BANNER_HEIGHT;
const ASPECT_TOLERANCE = 0.04;

const ACCEPTED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];

export function homeBannerPublicUrl(
  key: string | null | undefined,
  baseUrl: string,
): string | null {
  const trimmed = key?.trim();
  if (!trimmed) return null;
  return `${baseUrl.replace(/\/$/, '')}/${trimmed.replace(/^\//, '')}`;
}

/**
 * Returns an error message when the file cannot be the homepage banner.
 * Null means the size and type are acceptable.
 */
export function validateHomeBannerImage(file: {
  width: number;
  height: number;
  bytes: number;
  type: string;
}): string | null {
  const type = file.type.toLowerCase().split(';')[0].trim();
  if (!ACCEPTED_TYPES.includes(type)) {
    return 'Use a JPEG, PNG, or WebP image.';
  }
  if (!Number.isFinite(file.bytes) || file.bytes < 1) {
    return 'That file is empty.';
  }
  if (file.bytes > HOME_BANNER_MAX_BYTES) {
    return 'The image must be 3 MB or smaller.';
  }
  if (
    !Number.isFinite(file.width) ||
    !Number.isFinite(file.height) ||
    file.width < 1 ||
    file.height < 1
  ) {
    return 'Could not read the image size.';
  }
  if (file.width < HOME_BANNER_WIDTH || file.height < HOME_BANNER_HEIGHT) {
    return `This image is ${file.width} × ${file.height} px. Use at least ${HOME_BANNER_WIDTH} × ${HOME_BANNER_HEIGHT} px so it stays sharp on the homepage.`;
  }
  if (file.width > HOME_BANNER_MAX_WIDTH) {
    return `This image is ${file.width} px wide. Keep it at ${HOME_BANNER_MAX_WIDTH} px wide or narrower.`;
  }
  const aspect = file.width / file.height;
  if (Math.abs(aspect - TARGET_ASPECT) / TARGET_ASPECT > ASPECT_TOLERANCE) {
    return `This image is ${file.width} × ${file.height} px. Use ${HOME_BANNER_WIDTH} × ${HOME_BANNER_HEIGHT} px, a wide banner. A different shape is cropped on the homepage.`;
  }
  return null;
}

/** Some browsers leave `file.type` empty; the extension is enough to classify it. */
export function homeBannerFileType(file: { type: string; name: string }): string {
  const type = file.type.toLowerCase().split(';')[0].trim();
  if (type) return type;
  const name = file.name.toLowerCase();
  if (name.endsWith('.png')) return 'image/png';
  if (name.endsWith('.webp')) return 'image/webp';
  if (name.endsWith('.jpg') || name.endsWith('.jpeg')) return 'image/jpeg';
  return 'application/octet-stream';
}

export function readImageSize(file: File): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      const width = image.naturalWidth;
      const height = image.naturalHeight;
      URL.revokeObjectURL(url);
      resolve({ width, height });
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Could not read that image. Use a JPEG, PNG, or WebP file.'));
    };
    image.src = url;
  });
}
